import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import type { AdpEntry, PlayerTrend, ScheduleData, DkAdpTrendPoint } from "../types/draft";
import {
  API_ADP_URL,
  API_SCHEDULE_URL,
  SNAPSHOT_INDEX_URL,
  ADP_HISTORY_URL,
  LAST_UPDATED_URL,
  DEFAULT_RANKINGS_URL,
  DK_ADP_URL,
  DK_ADP_LIVE_URL,
  FETCH_TIMEOUT_MS,
} from "../config/api";
import { normalizePlayerName } from "../helpers/playerHelpers";
import { logger } from "../services/logger";

const DATE_FORMAT_RE = /^\d{4}-\d{2}-\d{2}$/;

// ── Context shape ─────────────────────────────────────────────────

interface AdpContextValue {
  adpData: AdpEntry[];
  adpLoading: boolean;
  adpError: string | null;
  adpHistory: PlayerTrend[];
  adpHistoryLoading: boolean;
  historyError: string | null;
  /** Normalized names of players flagged as rookies (from default-rankings.json). */
  rookieNames: Set<string>;
  snapshotDates: string[];
  snapshotDate: string;
  snapshotAdpLoading: boolean;
  effectiveAdpLookup: Map<string, number>;
  udAdpLookup: Map<string, number>;
  /** Normalized name → current DraftKings market ADP (from adp-draftkings.json). */
  dkAdpLookup: Map<string, number>;
  /** DK ADP trajectory (normalized name → {curr, week, open}) for trend charts. */
  dkAdpTrend: Record<string, DkAdpTrendPoint>;
  /** Normalized name → Underdog projected season points (live, from adp.json). */
  udProjectionLookup: Map<string, number>;
  schedule: ScheduleData | null;
  scheduleError: string | null;
  syncedAt: string | null;
  setSnapshotDate: (v: string) => void;
  retryAdp: () => void;
}

const AdpContext = createContext<AdpContextValue | undefined>(undefined);

// ── Provider ──────────────────────────────────────────────────────

export function AdpProvider({ children }: { children: React.ReactNode }) {
  const [adpData, setAdpData] = useState<AdpEntry[]>([]);
  const [adpLoading, setAdpLoading] = useState(true);
  const [adpError, setAdpError] = useState<string | null>(null);

  const [dkAdpData, setDkAdpData] = useState<AdpEntry[]>([]);
  // Live DK ADP trajectory (normalized name → {curr, week, open}) from the feed.
  const [liveDkAdp, setLiveDkAdp] = useState<Record<string, DkAdpTrendPoint>>({});

  const [adpHistory, setAdpHistory] = useState<PlayerTrend[]>([]);
  const [adpHistoryLoading, setAdpHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [rookieNames, setRookieNames] = useState<Set<string>>(new Set());

  const [schedule, setSchedule] = useState<ScheduleData | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const [retryTick, setRetryTick] = useState(0);
  const retryAdp = useCallback(() => {
    setAdpError(null);
    setAdpLoading(true);
    setRetryTick(t => t + 1);
  }, []);

  const [snapshotDates, setSnapshotDates] = useState<string[]>([]);
  const [snapshotDate, setSnapshotDateInternal] = useState("live");
  const [snapshotAdpData, setSnapshotAdpData] = useState<Record<string, string>[]>([]);
  const [snapshotAdpLoading, setSnapshotAdpLoading] = useState(false);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);

  // ── Initial parallel fetches ─────────────────────────────────────

  useEffect(() => {
    const adpCtrl = new AbortController();
    const schedCtrl = new AbortController();
    const snapCtrl = new AbortController();
    const histCtrl = new AbortController();
    const lastUpdCtrl = new AbortController();
    const rankCtrl = new AbortController();
    const dkCtrl = new AbortController();
    const dkLiveCtrl = new AbortController();

    const withTimeout = (ctrl: AbortController) => {
      const id = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
      return () => clearTimeout(id);
    };

    const clearAdp = withTimeout(adpCtrl);
    const clearSched = withTimeout(schedCtrl);
    const clearSnap = withTimeout(snapCtrl);
    const clearHist = withTimeout(histCtrl);
    const clearLastUpd = withTimeout(lastUpdCtrl);
    const clearRank = withTimeout(rankCtrl);
    const clearDk = withTimeout(dkCtrl);
    const clearDkLive = withTimeout(dkLiveCtrl);

    fetch(API_ADP_URL, { signal: adpCtrl.signal })
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch ADP data");
        return res.json();
      })
      .then((data: AdpEntry[]) => {
        setAdpData(data);
        setAdpLoading(false);
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          logger.error("ADP fetch failed", {
            endpoint: API_ADP_URL,
            errorName: err.name,
            errorMessage: err.message,
          });
          setAdpError("Unable to load ADP data. Some columns may be unavailable.");
          setAdpLoading(false);
        }
      })
      .finally(clearAdp);

    fetch(API_SCHEDULE_URL, { signal: schedCtrl.signal })
      .then(res => {
        if (!res.ok) throw new Error("Schedule not published yet");
        return res.json();
      })
      .then((data: Record<string, unknown>) => {
        if (data?.weekNums && data?.weekSchedules) setSchedule(data as unknown as ScheduleData);
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          logger.warn("Schedule fetch failed", {
            endpoint: API_SCHEDULE_URL,
            errorName: err.name,
            errorMessage: err.message,
          });
          setScheduleError("Unable to load schedule data. Stack analysis will use fallback data.");
        }
      })
      .finally(clearSched);

    fetch(SNAPSHOT_INDEX_URL, { signal: snapCtrl.signal })
      .then(res => (res.ok ? res.json() : []))
      .then((dates: unknown) => setSnapshotDates(Array.isArray(dates) ? dates : []))
      .catch(() => {})
      .finally(clearSnap);

    fetch(ADP_HISTORY_URL, { signal: histCtrl.signal })
      .then(res => (res.ok ? res.json() : []))
      .then((data: unknown) => {
        setAdpHistory(Array.isArray(data) ? (data as PlayerTrend[]) : []);
        setAdpHistoryLoading(false);
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          logger.warn("ADP history fetch failed", {
            endpoint: ADP_HISTORY_URL,
            errorName: err.name,
            errorMessage: err.message,
          });
          setHistoryError("Unable to load ADP history.");
          setAdpHistory([]);
          setAdpHistoryLoading(false);
        }
      })
      .finally(clearHist);

    fetch(DEFAULT_RANKINGS_URL, { signal: rankCtrl.signal })
      .then(res => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        if (!data || typeof data !== "object") return;
        const names = new Set<string>();
        for (const entry of Object.values(data as Record<string, unknown>)) {
          const e = entry as { name?: string; rookie?: boolean };
          if (e?.rookie && typeof e.name === "string") {
            const norm = normalizePlayerName(e.name);
            if (norm) names.add(norm);
          }
        }
        setRookieNames(names);
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          logger.warn("Default rankings fetch failed", {
            endpoint: DEFAULT_RANKINGS_URL,
            errorName: err.name,
            errorMessage: err.message,
          });
        }
      })
      .finally(clearRank);

    // DraftKings current ADP (same-origin public asset). Non-fatal: UD portfolios
    // never use it, and a DK portfolio degrades to the undrafted-ADP fallback.
    fetch(DK_ADP_URL, { signal: dkCtrl.signal })
      .then(res => (res.ok ? res.json() : []))
      .then((data: unknown) => setDkAdpData(Array.isArray(data) ? (data as AdpEntry[]) : []))
      .catch(err => {
        if (err.name !== "AbortError") {
          logger.warn("DK ADP fetch failed", {
            endpoint: DK_ADP_URL,
            errorName: err.name,
            errorMessage: err.message,
          });
        }
      })
      .finally(clearDk);

    // DraftKings CURRENT ADP — public CORS-open feed (Occupy Fantasy). Field-wide
    // and live; keyed by normalized name → curr_adp. Non-fatal: on failure the
    // bundled DK_ADP_URL floor above still applies.
    fetch(DK_ADP_LIVE_URL, { signal: dkLiveCtrl.signal })
      .then(res => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        const rows = (data as { adps?: Array<{ player_name?: string; curr_adp?: number; last_week_adp?: number; opener_adp?: number }> })?.adps;
        if (!Array.isArray(rows)) return;
        const round1 = (n: unknown): number | null =>
          typeof n === "number" && !isNaN(n) ? Math.round(n * 10) / 10 : null;
        const map: Record<string, DkAdpTrendPoint> = {};
        for (const r of rows) {
          const name = normalizePlayerName(r.player_name || "");
          const curr = round1(r.curr_adp);
          // curr_adp is required; the two history points are optional (→ null).
          if (name && curr !== null) {
            map[name] = { curr, week: round1(r.last_week_adp), open: round1(r.opener_adp) };
          }
        }
        setLiveDkAdp(map);
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          logger.warn("Live DK ADP fetch failed", {
            endpoint: DK_ADP_LIVE_URL,
            errorName: err.name,
            errorMessage: err.message,
          });
        }
      })
      .finally(clearDkLive);

    fetch(LAST_UPDATED_URL, { signal: lastUpdCtrl.signal })
      .then(res => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        if (data && typeof data === "object" && "syncedAt" in data && typeof (data as Record<string, unknown>).syncedAt === "string") {
          setSyncedAt((data as { syncedAt: string }).syncedAt);
        }
      })
      .catch(err => {
        if (err.name !== "AbortError") {
          logger.warn("Last-updated fetch failed", {
            endpoint: LAST_UPDATED_URL,
            errorName: err.name,
            errorMessage: err.message,
          });
        }
      })
      .finally(clearLastUpd);

    return () => {
      adpCtrl.abort();
      schedCtrl.abort();
      snapCtrl.abort();
      histCtrl.abort();
      lastUpdCtrl.abort();
      rankCtrl.abort();
      dkCtrl.abort();
      dkLiveCtrl.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryTick]);

  // ── Snapshot CSV fetch ───────────────────────────────────────────

  useEffect(() => {
    if (snapshotDate === "live") {
      setSnapshotAdpData([]);
      return;
    }
    let mounted = true;
    setSnapshotAdpLoading(true);
    Papa.parse(`/snapshots/adp_${snapshotDate}.csv`, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: result => {
        if (!mounted) return;
        setSnapshotAdpData((result.data as Record<string, string>[]) || []);
        setSnapshotAdpLoading(false);
      },
      error: () => {
        if (!mounted) return;
        setSnapshotAdpData([]);
        setSnapshotAdpLoading(false);
      },
    });
    return () => {
      mounted = false;
    };
  }, [snapshotDate]);

  // ── Validate and set snapshot date ───────────────────────────────

  const setSnapshotDate = useCallback((date: string) => {
    if (date !== "live") {
      // Reject non YYYY-MM-DD strings before any path construction
      if (!DATE_FORMAT_RE.test(date)) return;
      if (!snapshotDates.includes(date)) return;
    }
    setSnapshotDateInternal(date);
  }, [snapshotDates]);

  // ── ADP lookup maps ──────────────────────────────────────────────

  const udAdpLookup = useMemo(() => {
    const map = new Map<string, number>();
    adpData.forEach(row => {
      const name = normalizePlayerName(row.name || "");
      if (name) map.set(name, row.adp);
    });
    return map;
  }, [adpData]);

  const dkAdpLookup = useMemo(() => {
    const map = new Map<string, number>();
    // Bundled asset first (fallback floor)...
    dkAdpData.forEach(row => {
      const name = normalizePlayerName(row.name || "");
      if (name && typeof row.adp === "number" && !isNaN(row.adp)) map.set(name, row.adp);
    });
    // ...then the live public feed's current ADP wins. Already normalized.
    for (const [name, t] of Object.entries(liveDkAdp)) map.set(name, t.curr);
    return map;
  }, [dkAdpData, liveDkAdp]);

  const udProjectionLookup = useMemo(() => {
    const map = new Map<string, number>();
    adpData.forEach(row => {
      const name = normalizePlayerName(row.name || "");
      if (name && typeof row.projectedPoints === "number" && !isNaN(row.projectedPoints)) {
        map.set(name, row.projectedPoints);
      }
    });
    return map;
  }, [adpData]);

  const snapshotAdpLookup = useMemo(() => {
    const map = new Map<string, number>();
    snapshotAdpData.forEach(row => {
      const name = normalizePlayerName(`${row.firstName ?? ""} ${row.lastName ?? ""}`);
      if (name) map.set(name, parseFloat(row.adp));
    });
    return map;
  }, [snapshotAdpData]);

  const effectiveAdpLookup = useMemo(
    () => snapshotDate === "live" ? udAdpLookup : snapshotAdpLookup,
    [snapshotDate, udAdpLookup, snapshotAdpLookup]
  );

  const contextValue = useMemo(() => ({
    adpData,
    adpLoading,
    adpError,
    adpHistory,
    adpHistoryLoading,
    historyError,
    rookieNames,
    snapshotDates,
    snapshotDate,
    snapshotAdpLoading,
    effectiveAdpLookup,
    udAdpLookup,
    dkAdpLookup,
    dkAdpTrend: liveDkAdp,
    udProjectionLookup,
    schedule,
    scheduleError,
    syncedAt,
    setSnapshotDate,
    retryAdp,
  }), [
    adpData, adpLoading, adpError, adpHistory, adpHistoryLoading,
    historyError, rookieNames, snapshotDates, snapshotDate, snapshotAdpLoading,
    effectiveAdpLookup, udAdpLookup, dkAdpLookup, liveDkAdp, udProjectionLookup, schedule, scheduleError,
    syncedAt, setSnapshotDate, retryAdp,
  ]);

  return (
    <AdpContext.Provider value={contextValue}>
      {children}
    </AdpContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────

export function useAdp(): AdpContextValue {
  const ctx = useContext(AdpContext);
  if (!ctx) throw new Error("useAdp must be used within AdpProvider");
  return ctx;
}
